import React, { useState } from 'react';
import { X, Plus, Trash2, Link2, Mail, Phone, AtSign, Edit2, Check, XCircle, Send, CheckSquare, Square, EyeOff, ClipboardList } from 'lucide-react';
import NotificationToast from '../NotificationToast';
import { updateInvite, setBlindBidAccess, setUserAccess } from '../../services/supabaseService';
import { EMAIL_CONFIG } from '../../config/emailConfig';
import AdminBuyerProfileModal from './AdminBuyerProfileModal';

/**
 * Modal para gestionar invitados de una propiedad
 * VERSIÓN CON SUPABASE + ENVÍO DE EMAILS + EDICIÓN INLINE
 */
const ManageInvitesModal = ({ property, invites, bids, onSaveInvites, onClose, saving = false }) => {
  const [newInvite, setNewInvite] = useState({ name: '', email: '', phone: '' });
  const [copiedId, setCopiedId] = useState(null);
  const [localSaving, setLocalSaving] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});

  // Estados para email masivo
  const [selectedInvites, setSelectedInvites] = useState(new Set());
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailMessage, setBulkEmailMessage] = useState('');
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  const [confirmingBlindBidId, setConfirmingBlindBidId] = useState(null);
  const [profileModalInvite, setProfileModalInvite] = useState(null);

  const propertyInvites = invites.filter(i => i.propertyId === property.id);
  const propertyBids = bids
    .filter(b => b.propertyId === property.id)
    .sort((a, b) => b.amount - a.amount || new Date(a.timestamp) - new Date(b.timestamp));
  const isSaving = saving || localSaving;

  const generateToken = () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  };

  const showNotification = (type, title, message) => {
    setNotification({ type, title, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const sendInviteEmail = async (invite, url) => {
    console.log('🚀 Enviando email a:', invite.email);

    try {
      const payload = {
        name: invite.name,
        email: invite.email,
        propertyTitle: property.title,
        propertyDescription: property.description,
        inviteUrl: url,
        deadline: property.deadline
      };

      console.log('📧 Payload:', payload);

      const response = await fetch('/api/send-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      console.log('📨 Status:', response.status);
      const data = await response.json();
      console.log('📨 Response:', data);

      if (!response.ok) throw new Error(data.error || 'Error al enviar email');
      return true;
    } catch (error) {
      console.error('❌ Error:', error);
      return false;
    }
  };

  const handleAddInvite = async (e, sendEmail = false) => {
    e.preventDefault();
    setError('');
    setLocalSaving(true);

    try {
      const invite = {
        id: Date.now().toString(),
        propertyId: property.id,
        token: generateToken(),
        ...newInvite,
        createdAt: new Date().toISOString()
      };

      const url = `${window.location.origin}/property/${property.id}?token=${invite.token}`;

      await onSaveInvites([...invites, invite]);

      setNewInvite({ name: '', email: '', phone: '' });

      if (sendEmail) {
        setSendingEmail(true);
        const emailSent = await sendInviteEmail(invite, url);
        setSendingEmail(false);

        if (emailSent) {
          showNotification('success', '¡Invitación enviada!', `Email enviado a ${invite.email}`);
        } else {
          showNotification('warning', 'Invitado agregado', `${invite.name} agregado. Email NO enviado - revisa consola`);
        }
      } else {
        showNotification('success', 'Invitado agregado', `${invite.name} agregado correctamente. Puedes enviarle el enlace manualmente.`);
      }
    } catch (error) {
      console.error('Error agregando invitado:', error);
      showNotification('error', 'Error al agregar', error.message || 'No se pudo agregar el invitado. Puede que ya exista.');
      setSendingEmail(false);
    } finally {
      setLocalSaving(false);
    }
  };

  const handleResendEmail = async (invite) => {
    setResendingId(invite.id);
    const url = `${window.location.origin}/property/${property.id}?token=${invite.token}`;

    const emailSent = await sendInviteEmail(invite, url);
    setResendingId(null);

    if (emailSent) {
      showNotification('success', '¡Email reenviado!', `Email enviado nuevamente a ${invite.email}`);
    } else {
      showNotification('error', 'Error al reenviar', `No se pudo enviar el email. Revisa la consola.`);
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    const confirmed = window.confirm('¿Eliminar este interesado?');
    if (!confirmed) return;

    setError('');
    setLocalSaving(true);

    try {
      await onSaveInvites(invites.filter(i => i.id !== inviteId));
      showNotification('success', 'Eliminado', 'Interesado eliminado correctamente');
    } catch (error) {
      console.error('Error eliminando invitado:', error);
      setError(error.message || 'Error al eliminar invitado. Intenta de nuevo.');
    } finally {
      setLocalSaving(false);
    }
  };

  const handleStartEdit = (invite) => {
    setEditingId(invite.id);
    setEditingData({
      name: invite.name,
      phone: invite.phone || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleSaveEdit = async (inviteId) => {
    setLocalSaving(true);
    try {
      // Actualizar en Supabase
      await updateInvite(inviteId, editingData);

      // Actualizar en estado local
      const updatedInvites = invites.map(inv =>
        inv.id === inviteId ? { ...inv, ...editingData } : inv
      );
      await onSaveInvites(updatedInvites);

      setEditingId(null);
      setEditingData({});
      showNotification('success', 'Actualizado', 'Invitado actualizado correctamente');
    } catch (error) {
      console.error('Error actualizando:', error);
      showNotification('error', 'Error', 'No se pudo actualizar el invitado');
    } finally {
      setLocalSaving(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getBidderStatus = (email) => {
    const userBids = propertyBids.filter(b => b.buyerEmail === email);

    if (userBids.length === 0) {
      return { text: 'Sin ofertas', color: 'text-slate-400' };
    }

    // El primer bid del array ya es el ganador (ordenado por amount DESC, timestamp ASC)
    const topBid = propertyBids[0];
    // Calcular oferta más alta del usuario
    const userHighest = Math.max(...userBids.map(b => b.amount));

    // Verificar si tiene oferta de última oportunidad
    const hasLastChanceBid = userBids.some(b => b.is_last_chance);

    // Total de ofertas
    const totalText = userBids.length === 1 ? '1 oferta' : `${userBids.length} ofertas`;

    // Si tiene la oferta más alta (comparar por ID para desempate correcto)
    if (topBid && userBids.some(b => b.id === topBid.id)) {
      return {
        text: `${totalText} • Oferta más alta`,
        color: 'text-green-400'
      };
    }

    // Si tiene oferta de última oportunidad
    if (hasLastChanceBid) {
      return {
        text: `${totalText} • Última: ${userHighest.toLocaleString()}€`,
        color: 'text-orange-400'
      };
    }

    // Si no es la más alta
    return {
      text: `${totalText} • Última: ${userHighest.toLocaleString()}€`,
      color: 'text-amber-400'
    };
  };

  // ============================================
  // FUNCIONES PARA EMAIL MASIVO
  // ============================================

  const toggleSelectInvite = (inviteId) => {
    const newSelected = new Set(selectedInvites);
    if (newSelected.has(inviteId)) {
      newSelected.delete(inviteId);
    } else {
      newSelected.add(inviteId);
    }
    setSelectedInvites(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedInvites.size === propertyInvites.length) {
      setSelectedInvites(new Set());
    } else {
      setSelectedInvites(new Set(propertyInvites.map(i => i.id)));
    }
  };

  const handleOpenBulkEmail = () => {
    setBulkEmailSubject(`Información sobre ${property.title}`);
    setBulkEmailMessage('');
    setShowBulkEmailModal(true);
  };

  const handleCloseBulkEmail = () => {
    setShowBulkEmailModal(false);
    setBulkEmailSubject('');
    setBulkEmailMessage('');
  };

  const handleSendBulkEmail = async () => {
    if (!bulkEmailMessage.trim()) {
      showNotification('error', 'Error', 'Escribe un mensaje para enviar');
      return;
    }

    const selectedRecipients = propertyInvites
      .filter(inv => selectedInvites.has(inv.id))
      .map(inv => ({
        name: inv.name,
        email: inv.email,
        token: inv.token
      }));

    if (selectedRecipients.length === 0) {
      showNotification('error', 'Error', 'No hay destinatarios seleccionados');
      return;
    }

    setSendingBulkEmail(true);

    try {
      const response = await fetch('/api/send-bulk-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipients: selectedRecipients,
          subject: bulkEmailSubject,
          customMessage: bulkEmailMessage,
          propertyId: property.id,
          propertyTitle: property.title,
          baseUrl: window.location.origin
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al enviar emails');
      }

      if (data.errors > 0) {
        showNotification('warning', 'Envío parcial', `${data.sent} enviados, ${data.errors} fallidos`);
      } else {
        showNotification('success', '¡Emails enviados!', `${data.sent} emails enviados correctamente`);
      }

      handleCloseBulkEmail();
      setSelectedInvites(new Set());
    } catch (error) {
      console.error('Error enviando emails:', error);
      showNotification('error', 'Error', error.message || 'No se pudieron enviar los emails');
    } finally {
      setSendingBulkEmail(false);
    }
  };

  // Handler para habilitar oferta ciega anticipada por fila (operación irreversible)
  const handleEnableBlindBid = async (inviteId) => {
    setConfirmingBlindBidId(null);
    try {
      await setBlindBidAccess([inviteId]);

      const updatedInvites = invites.map(inv =>
        inv.id === inviteId ? { ...inv, canBlindBid: true } : inv
      );
      await onSaveInvites(updatedInvites);

      showNotification('success', 'Habilitado', 'El invitado puede enviar ofertas ciegas anticipadas');
    } catch (error) {
      console.error('Error habilitando acceso:', error);
      showNotification('error', 'Error', 'No se pudo habilitar el acceso');
    }
  };

  const handleToggleAccess = async (invite) => {
    const newValue = !invite.hasAccess;
    try {
      await setUserAccess(invite.id, newValue);
      const updatedInvites = invites.map(inv =>
        inv.id === invite.id ? { ...inv, hasAccess: newValue } : inv
      );
      await onSaveInvites(updatedInvites);
      showNotification(
        'success',
        newValue ? 'Acceso concedido' : 'Acceso revocado',
        `${invite.name} ${newValue ? 'puede acceder' : 'ya no puede acceder'}`
      );
    } catch (error) {
      console.error('Error actualizando acceso:', error);
      showNotification('error', 'Error', 'No se pudo actualizar el acceso');
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-slate-700">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Gestionar Interesados</h2>
              <p className="text-slate-400">{property.title}</p>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="text-slate-400 hover:text-white transition-all disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Contenido */}
          <div className="p-6 overflow-y-auto flex-1">
            {/* Formulario para agregar nuevo interesado */}
            <form onSubmit={(e) => handleAddInvite(e, false)} className="mb-6 bg-slate-900/50 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Agregar nuevo interesado</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  required
                  disabled={isSaving}
                  value={newInvite.name}
                  onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                  className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50"
                  placeholder="Nombre completo"
                />
                <input
                  type="email"
                  required
                  disabled={isSaving}
                  value={newInvite.email}
                  onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                  className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50"
                  placeholder="email@ejemplo.com"
                />
                <input
                  type="tel"
                  disabled={isSaving}
                  value={newInvite.phone}
                  onChange={(e) => setNewInvite({ ...newInvite, phone: e.target.value })}
                  className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50"
                  placeholder="Teléfono (opcional)"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="bg-slate-700 text-white font-semibold py-2 rounded-lg hover:bg-slate-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Agregar
                </button>
                <button
                  type="button"
                  onClick={(e) => handleAddInvite(e, true)}
                  disabled={isSaving}
                  className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold py-2 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sendingEmail ? (
                    <>
                      <div className="w-4 h-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Agregar y Enviar
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Mensaje de error */}
            {error && (
              <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Lista de interesados */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={toggleSelectAll}
                    disabled={propertyInvites.length === 0 || isSaving}
                    className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
                  >
                    {selectedInvites.size === propertyInvites.length && propertyInvites.length > 0 ? (
                      <CheckSquare className="w-5 h-5 text-amber-400" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                    <span className="text-sm">Seleccionar todos</span>
                  </button>
                  <h3 className="text-lg font-semibold text-white">
                    Interesados ({propertyInvites.length})
                  </h3>
                </div>

                {selectedInvites.size > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleOpenBulkEmail}
                      disabled={isSaving}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-all disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                      <span>Comunicado ({selectedInvites.size})</span>
                    </button>
                  </div>
                )}
              </div>

              {propertyInvites.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  No hay interesados todavía
                </div>
              ) : (
                propertyInvites.map(invite => {
                  const url = `${window.location.origin}/property/${property.id}?token=${invite.token}`;
                  const status = getBidderStatus(invite.email);
                  const isResending = resendingId === invite.id;
                  const isEditing = editingId === invite.id;

                  return (
                    <div key={invite.id} className="bg-slate-900/30 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        {/* Checkbox de selección */}
                        <button
                          onClick={() => toggleSelectInvite(invite.id)}
                          disabled={isSaving}
                          className="mr-3 mt-1 flex-shrink-0 text-slate-400 hover:text-amber-400 transition-colors disabled:opacity-50"
                        >
                          {selectedInvites.has(invite.id) ? (
                            <CheckSquare className="w-5 h-5 text-amber-400" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>

                        <div className="flex-1">
                          {isEditing ? (
                            /* MODO EDICIÓN */
                            <div className="space-y-2 pr-4">
                              <input
                                type="text"
                                value={editingData.name}
                                onChange={(e) => setEditingData({...editingData, name: e.target.value})}
                                className="w-full bg-slate-800 border border-amber-500 rounded px-3 py-1.5 text-white text-sm"
                                placeholder="Nombre"
                              />
                              <input
                                type="tel"
                                value={editingData.phone}
                                onChange={(e) => setEditingData({...editingData, phone: e.target.value})}
                                className="w-full bg-slate-800 border border-amber-500 rounded px-3 py-1.5 text-white text-sm"
                                placeholder="Teléfono (opcional)"
                              />
                            </div>
                          ) : (
                            /* MODO VISTA NORMAL */
                            <>
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-semibold text-white">{invite.name}</span>
                                <span className={`text-sm ${status.color}`}>{status.text}</span>
                                {invite.canBlindBid ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs text-purple-400">
                                    <EyeOff className="w-3 h-3" />
                                    Oferta ciega
                                  </span>
                                ) : confirmingBlindBidId === invite.id ? (
                                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-purple-500/10 border border-purple-500/40 rounded-full text-xs text-purple-300">
                                    <EyeOff className="w-3 h-3" />
                                    <span>¿Activar?</span>
                                    <button
                                      onClick={() => handleEnableBlindBid(invite.id)}
                                      className="text-purple-300 hover:text-green-400 transition-colors"
                                      title="Confirmar"
                                    >
                                      <Check className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => setConfirmingBlindBidId(null)}
                                      className="text-slate-500 hover:text-slate-300 transition-colors"
                                      title="Cancelar"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => setConfirmingBlindBidId(invite.id)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-slate-700 text-slate-500 hover:border-purple-500/50 hover:text-purple-400 transition-colors"
                                    title="Habilitar acceso anticipado a fase ciega"
                                  >
                                    <EyeOff className="w-3 h-3" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleToggleAccess(invite)}
                                  disabled={isSaving}
                                  title={invite.hasAccess ? 'Revocar acceso' : 'Conceder acceso'}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors disabled:opacity-50 ${
                                    invite.hasAccess
                                      ? 'bg-green-500/20 border-green-500/30 text-green-400 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400'
                                      : 'bg-red-500/20 border-red-500/30 text-red-400 hover:bg-green-500/20 hover:border-green-500/30 hover:text-green-400'
                                  }`}
                                >
                                  {invite.hasAccess ? '✓ Acceso' : '✗ Bloqueado'}
                                </button>
                              </div>
                              <div className="flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-2 text-slate-400">
                                  <AtSign className="w-4 h-4" />
                                  <span>{invite.email}</span>
                                </div>
                                {invite.phone && (
                                  <div className="flex items-center gap-2 text-slate-500">
                                    <Phone className="w-4 h-4" />
                                    <span>{invite.phone}</span>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Botones de acción */}
                        <div className="flex gap-2 ml-4">
                          {isEditing ? (
                            /* BOTONES DE GUARDAR/CANCELAR */
                            <>
                              <button
                                onClick={() => handleSaveEdit(invite.id)}
                                disabled={localSaving}
                                className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg transition-all disabled:opacity-50"
                                title="Guardar"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                disabled={localSaving}
                                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-50"
                                title="Cancelar"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            /* BOTONES NORMALES */
                            <>
                              <button
                                onClick={() => handleStartEdit(invite)}
                                disabled={isSaving}
                                className="p-2 text-amber-400 hover:bg-amber-500/10 rounded-lg transition-all disabled:opacity-50"
                                title="Editar nombre / teléfono"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setProfileModalInvite(invite)}
                                disabled={isSaving}
                                className="p-2 text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-all disabled:opacity-50"
                                title="Completar / editar perfil del comprador"
                              >
                                <ClipboardList className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleResendEmail(invite)}
                                disabled={isResending || isSaving}
                                className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all disabled:opacity-50"
                                title="Reenviar email"
                              >
                                {isResending ? (
                                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteInvite(invite.id)}
                                disabled={isSaving}
                                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-50"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* URL de invitación */}
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          readOnly
                          value={url}
                          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-slate-300 text-sm"
                        />
                        <div className="relative">
                          <button
                            onClick={() => copyToClipboard(url, invite.id)}
                            disabled={isSaving}
                            className="flex items-center gap-2 bg-amber-500/10 text-amber-400 px-4 py-2 rounded-lg hover:bg-amber-500/20 transition-all disabled:opacity-50"
                          >
                            <Link2 className="w-4 h-4" />
                            Copiar
                          </button>
                          {copiedId === invite.id && (
                            <div className="absolute top-0 right-0 -mt-8 bg-green-500 text-white text-xs px-3 py-1 rounded shadow-lg">
                              ✓ Copiado
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-slate-700">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="w-full bg-slate-700 text-white font-medium py-3 rounded-lg hover:bg-slate-600 transition-all disabled:opacity-50"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Componente de notificaciones */}
      {notification && (
        <NotificationToast
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Modal de Email Masivo */}
      {showBulkEmailModal && (
        <>
          <div
            className="fixed inset-0 bg-black/70 z-[60]"
            onClick={handleCloseBulkEmail}
          />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div
              className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-slate-700">
                <div>
                  <h3 className="text-xl font-bold text-white">Enviar comunicado</h3>
                  <p className="text-sm text-slate-400 mt-1">
                    A {selectedInvites.size} destinatario{selectedInvites.size !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={handleCloseBulkEmail}
                  disabled={sendingBulkEmail}
                  className="text-slate-400 hover:text-white transition-colors disabled:opacity-50"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Contenido */}
              <div className="p-5 space-y-4">
                {/* Asunto */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Asunto
                  </label>
                  <input
                    type="text"
                    value={bulkEmailSubject}
                    onChange={(e) => setBulkEmailSubject(e.target.value)}
                    disabled={sendingBulkEmail}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 disabled:opacity-50"
                    placeholder="Asunto del email"
                  />
                </div>

                {/* Mensaje */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Mensaje
                  </label>
                  <textarea
                    value={bulkEmailMessage}
                    onChange={(e) => setBulkEmailMessage(e.target.value)}
                    disabled={sendingBulkEmail}
                    rows={6}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 disabled:opacity-50 resize-none"
                    placeholder="Escribe tu mensaje aquí..."
                  />
                </div>

                {/* Info */}
                <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                  <p className="text-xs text-slate-400">
                    <span className="text-amber-400">ℹ️</span> El enlace personal de acceso se añadirá automáticamente al final del email.
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Se enviará desde: <span className="text-slate-300">{EMAIL_CONFIG.info}</span>
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-5 border-t border-slate-700">
                <button
                  onClick={handleCloseBulkEmail}
                  disabled={sendingBulkEmail}
                  className="flex-1 bg-slate-700 text-white font-medium py-2.5 rounded-lg hover:bg-slate-600 transition-all disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendBulkEmail}
                  disabled={sendingBulkEmail || !bulkEmailMessage.trim()}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-2.5 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sendingBulkEmail ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Modal de perfil del comprador */}
      {profileModalInvite && (
        <AdminBuyerProfileModal
          invite={profileModalInvite}
          property={property}
          onClose={() => setProfileModalInvite(null)}
          onSaved={() => showNotification('success', 'Perfil guardado', `Perfil de ${profileModalInvite.name} actualizado correctamente`)}
        />
      )}
    </>
  );
};

export default ManageInvitesModal;
